import { useEffect } from 'react';
import { applyMistRailStack, shouldHideMistRail } from './mistRail.js';

type Axis = 'y' | 'x';

type RailEntry = {
  el: HTMLElement;
  axis: Axis;
  rail: HTMLDivElement;
  thumb: HTMLDivElement;
  dragging: boolean;
  dragOffset: number;
};

/** Explicit + common scroll hosts — discovered without waiting for pointerover. */
const MIST_HOST_SELECTOR = [
  '[data-wp-mist-scroll]',
  '[data-wp-scroll-area]',
  '.wp-scroll',
  '.wp-scroll-fade',
  '.wp-scroll-fade-b',
  '.wp-scroll-fade-x',
  '[data-wp-datatable-root]',
].join(',');

/** Scopes scanned for any overflow:auto/scroll host (covers unmarked CSS modules). */
const MIST_SCOPE_SELECTOR = [
  '#admin-main-content',
  '[data-wp-outlet-frame]',
  '[data-wp-overlay-open]',
  '[data-wp-scroll-area-root]',
  '[role="dialog"]',
  '[role="menu"]',
  '[role="listbox"]',
  '[role="tooltip"]',
].join(',');

const OVERFLOW_RE = /^(auto|scroll)$/;

function elementOverflowScrolls(el: HTMLElement): boolean {
  const { overflow, overflowX, overflowY } = getComputedStyle(el);
  return (
    OVERFLOW_RE.test(overflowY) ||
    OVERFLOW_RE.test(overflowX) ||
    OVERFLOW_RE.test(overflow)
  );
}

function walkOverflowHosts(scope: ParentNode, into: Set<HTMLElement>, budget: { n: number }) {
  if (budget.n <= 0) return;
  const nodes = scope.querySelectorAll('*');
  for (let i = 0; i < nodes.length && budget.n > 0; i++) {
    const node = nodes[i];
    if (!(node instanceof HTMLElement)) continue;
    // Skip huge inert trees
    if (node.hasAttribute('hidden') || node.getAttribute('aria-hidden') === 'true') continue;
    if (!elementOverflowScrolls(node)) continue;
    if (isScrollableY(node) || isScrollableX(node)) {
      into.add(node);
      budget.n -= 1;
    }
  }
}

const elIds = new WeakMap<HTMLElement, string>();
let idSeq = 0;

function elId(el: HTMLElement): string {
  if (el === document.documentElement || el === document.body) return 'doc';
  let id = elIds.get(el);
  if (!id) {
    id = `m${++idSeq}`;
    elIds.set(el, id);
  }
  return id;
}

function railMapKey(el: HTMLElement, axis: Axis): string {
  return `${axis}:${elId(el)}`;
}

function prefersTouchPrimary(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: none), (pointer: coarse)').matches &&
    !window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
}

function overflowAllows(el: HTMLElement, axis: Axis): boolean {
  if (el === document.documentElement || el === document.body) return true;
  const { overflow, overflowY, overflowX } = getComputedStyle(el);
  if (axis === 'y') {
    return (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflow === 'auto' ||
      overflow === 'scroll'
    );
  }
  return (
    overflowX === 'auto' ||
    overflowX === 'scroll' ||
    overflow === 'auto' ||
    overflow === 'scroll'
  );
}

function isScrollableY(el: HTMLElement): boolean {
  if (!overflowAllows(el, 'y')) return false;
  if (el === document.documentElement || el === document.body) {
    return document.documentElement.scrollHeight > document.documentElement.clientHeight + 2;
  }
  return el.scrollHeight > el.clientHeight + 2;
}

function isScrollableX(el: HTMLElement): boolean {
  if (!overflowAllows(el, 'x')) return false;
  if (el === document.documentElement || el === document.body) {
    return document.documentElement.scrollWidth > document.documentElement.clientWidth + 2;
  }
  return el.scrollWidth > el.clientWidth + 2;
}

function verticalMetrics(el: HTMLElement) {
  if (el === document.documentElement || el === document.body) {
    const view = window.innerHeight;
    const total = Math.max(
      document.documentElement.scrollHeight,
      document.body?.scrollHeight ?? 0,
    );
    const limit = Math.max(0, total - view);
    const thumbSize = Math.max(40, Math.min(view * 0.45, total > 0 ? (view / total) * view : 40));
    const maxTravel = Math.max(0, view - thumbSize);
    return { view, total, limit, thumbSize, maxTravel };
  }
  const view = el.clientHeight;
  const total = el.scrollHeight;
  const limit = Math.max(0, total - view);
  const thumbSize = Math.max(40, Math.min(view * 0.45, total > 0 ? (view / total) * view : 40));
  const maxTravel = Math.max(0, view - thumbSize);
  return { view, total, limit, thumbSize, maxTravel };
}

function horizontalMetrics(el: HTMLElement) {
  if (el === document.documentElement || el === document.body) {
    const view = window.innerWidth;
    const total = Math.max(
      document.documentElement.scrollWidth,
      document.body?.scrollWidth ?? 0,
    );
    const limit = Math.max(0, total - view);
    const thumbSize = Math.max(40, Math.min(view * 0.45, total > 0 ? (view / total) * view : 40));
    const maxTravel = Math.max(0, view - thumbSize);
    return { view, total, limit, thumbSize, maxTravel };
  }
  const view = el.clientWidth;
  const total = el.scrollWidth;
  const limit = Math.max(0, total - view);
  const thumbSize = Math.max(40, Math.min(view * 0.45, total > 0 ? (view / total) * view : 40));
  const maxTravel = Math.max(0, view - thumbSize);
  return { view, total, limit, thumbSize, maxTravel };
}

/** ScrollArea root — rail anchors here so it tracks column layout, not the viewport. */
function scrollAreaAnchor(el: HTMLElement): HTMLElement | null {
  const parent = el.parentElement;
  if (parent?.hasAttribute('data-wp-scroll-area-root')) return parent;
  return null;
}

function pinRailToAnchor(rail: HTMLDivElement, anchor: HTMLElement, axis: Axis) {
  if (rail.parentElement !== anchor) anchor.appendChild(rail);
  rail.style.position = 'absolute';
  if (axis === 'y') {
    rail.style.top = '0';
    rail.style.bottom = '0';
    rail.style.right = '0';
    rail.style.left = 'auto';
    rail.style.height = 'auto';
    rail.style.width = '';
    return;
  }
  rail.style.left = '0';
  rail.style.right = '0';
  rail.style.bottom = '0';
  rail.style.top = 'auto';
  rail.style.width = 'auto';
  rail.style.height = '';
}

function readScroll(el: HTMLElement, axis: Axis) {
  if (el === document.documentElement || el === document.body) {
    return axis === 'y'
      ? window.scrollY || document.documentElement.scrollTop || 0
      : window.scrollX || document.documentElement.scrollLeft || 0;
  }
  return axis === 'y' ? el.scrollTop : el.scrollLeft;
}

function writeScroll(el: HTMLElement, axis: Axis, value: number) {
  if (el === document.documentElement || el === document.body) {
    if (axis === 'y') window.scrollTo(window.scrollX, value);
    else window.scrollTo(value, window.scrollY);
    return;
  }
  if (axis === 'y') el.scrollTop = value;
  else el.scrollLeft = value;
}

/**
 * Hide native OS scrollbars; attach mist thumbs (FF: quiet until hover, always when overflow).
 * Linux Chrome cannot restyle GTK trail/arrows — kill native, paint thumb only.
 * Touch-primary: no mist rails (platform scroll physics).
 */
export function useScrollbarReveal() {
  useEffect(() => {
    if (prefersTouchPrimary()) return;

    const rails = new Map<string, RailEntry>();
    let raf = 0;
    let dragKey: string | null = null;
    let ro: ResizeObserver | null = null;

    const prune = () => {
      for (const [key, entry] of rails) {
        if (!entry.el.isConnected) {
          entry.rail.remove();
          rails.delete(key);
        }
      }
    };

    const ensure = (el: HTMLElement, axis: Axis): RailEntry => {
      const key = railMapKey(el, axis);
      let entry = rails.get(key);
      if (entry) return entry;

      const rail = document.createElement('div');
      rail.className = axis === 'y' ? 'wp-mist-rail' : 'wp-mist-rail-x';
      rail.setAttribute('aria-hidden', 'true');
      rail.hidden = true;
      const thumb = document.createElement('div');
      thumb.className = 'wp-mist-thumb';
      rail.appendChild(thumb);
      const anchor = scrollAreaAnchor(el);
      if (anchor) anchor.appendChild(rail);
      else document.body.appendChild(rail);
      ro?.observe(el);

      entry = { el, axis, rail, thumb, dragging: false, dragOffset: 0 };
      rails.set(key, entry);

      thumb.addEventListener('pointerdown', (e) => {
        entry!.dragging = true;
        dragKey = key;
        thumb.setPointerCapture(e.pointerId);
        const box = thumb.getBoundingClientRect();
        entry!.dragOffset = axis === 'y' ? e.clientY - box.top : e.clientX - box.left;
        rail.classList.add('is-dragging');
        e.preventDefault();
        e.stopPropagation();
      });

      thumb.addEventListener('pointermove', (e) => {
        if (!entry!.dragging || dragKey !== key) return;
        const m = axis === 'y' ? verticalMetrics(el) : horizontalMetrics(el);
        const pos =
          axis === 'y'
            ? Math.max(0, Math.min(m.maxTravel, e.clientY - entry!.dragOffset))
            : Math.max(0, Math.min(m.maxTravel, e.clientX - entry!.dragOffset));
        const scroll = m.maxTravel > 0 ? (pos / m.maxTravel) * m.limit : 0;
        writeScroll(el, axis, scroll);
        if (axis === 'y') {
          thumb.style.height = `${m.thumbSize}px`;
          thumb.style.transform = `translate3d(0, ${pos}px, 0)`;
        } else {
          thumb.style.width = `${m.thumbSize}px`;
          thumb.style.transform = `translate3d(${pos}px, 0, 0)`;
        }
      });

      const endDrag = (e: PointerEvent) => {
        if (!entry!.dragging) return;
        entry!.dragging = false;
        dragKey = null;
        rail.classList.remove('is-dragging');
        try {
          thumb.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      };
      thumb.addEventListener('pointerup', endDrag);
      thumb.addEventListener('pointercancel', endDrag);

      return entry;
    };

    const hideAxis = (el: HTMLElement, axis: Axis) => {
      const existing = rails.get(railMapKey(el, axis));
      if (existing) existing.rail.hidden = true;
    };

    const paintAxis = (el: HTMLElement, axis: Axis) => {
      if (!el.isConnected || shouldHideMistRail(el)) {
        hideAxis(el, axis);
        return;
      }
      const scrollable = axis === 'y' ? isScrollableY(el) : isScrollableX(el);
      if (!scrollable) {
        hideAxis(el, axis);
        return;
      }

      const { rail, thumb, dragging } = ensure(el, axis);
      const m = axis === 'y' ? verticalMetrics(el) : horizontalMetrics(el);
      if (m.total <= m.view + 2) {
        rail.hidden = true;
        return;
      }

      applyMistRailStack(rail, el);

      const isDoc = el === document.documentElement || el === document.body;

      if (isDoc) {
        rail.hidden = false;
        if (axis === 'y') {
          rail.style.top = '0px';
          rail.style.height = `${window.innerHeight}px`;
          rail.style.right = '0px';
          rail.style.left = '';
          rail.style.width = '';
          rail.style.bottom = '';
        } else {
          rail.style.left = '0px';
          rail.style.width = `${window.innerWidth}px`;
          rail.style.bottom = '0px';
          rail.style.top = '';
          rail.style.height = '';
          rail.style.right = '';
        }
      } else {
        const rect = el.getBoundingClientRect();
        if (rect.height < 16 || rect.width < 16) {
          rail.hidden = true;
          return;
        }
        if (
          rect.bottom < 0 ||
          rect.top > window.innerHeight ||
          rect.right < 0 ||
          rect.left > window.innerWidth
        ) {
          rail.hidden = true;
          return;
        }

        rail.hidden = false;
        const anchor = scrollAreaAnchor(el);
        if (anchor) {
          pinRailToAnchor(rail, anchor, axis);
        } else if (axis === 'y') {
          rail.style.position = 'fixed';
          rail.style.top = `${rect.top}px`;
          rail.style.height = `${rect.height}px`;
          rail.style.right = `${Math.max(0, window.innerWidth - rect.right)}px`;
          rail.style.left = '';
          rail.style.width = '';
          rail.style.bottom = '';
        } else {
          rail.style.position = 'fixed';
          rail.style.left = `${rect.left}px`;
          rail.style.width = `${rect.width}px`;
          rail.style.bottom = `${Math.max(0, window.innerHeight - rect.bottom)}px`;
          rail.style.top = '';
          rail.style.height = '';
          rail.style.right = '';
        }
      }

      if (!dragging) {
        const travel = m.limit > 0 ? (readScroll(el, axis) / m.limit) * m.maxTravel : 0;
        if (axis === 'y') {
          thumb.style.height = `${m.thumbSize}px`;
          thumb.style.width = '';
          thumb.style.transform = `translate3d(0, ${travel}px, 0)`;
        } else {
          thumb.style.width = `${m.thumbSize}px`;
          thumb.style.height = '';
          thumb.style.transform = `translate3d(${travel}px, 0, 0)`;
        }
      }
    };

    const paint = (el: HTMLElement) => {
      paintAxis(el, 'y');
      paintAxis(el, 'x');
    };

    const collectHosts = (): HTMLElement[] => {
      const set = new Set<HTMLElement>();
      if (isScrollableY(document.documentElement) || isScrollableX(document.documentElement)) {
        set.add(document.documentElement);
      }
      document.querySelectorAll(MIST_HOST_SELECTOR).forEach((node) => {
        if (node instanceof HTMLElement) set.add(node);
      });
      const budget = { n: 80 };
      document.querySelectorAll(MIST_SCOPE_SELECTOR).forEach((scope) => {
        walkOverflowHosts(scope, set, budget);
      });
      for (const entry of rails.values()) set.add(entry.el);
      return [...set];
    };

    const paintAll = () => {
      raf = 0;
      prune();
      for (const el of collectHosts()) paint(el);
    };

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(paintAll);
    };

    const onScroll = (e: Event) => {
      const t = e.target;
      if (t === document || t === document.documentElement || t === document.body) {
        paint(document.documentElement);
        schedule();
        return;
      }
      if (t instanceof HTMLElement) {
        paint(t);
        schedule();
      }
    };

    const onOver = (e: Event) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      let node: Element | null = t;
      while (node && node !== document.body) {
        if (node instanceof HTMLElement && (isScrollableY(node) || isScrollableX(node))) {
          paint(node);
        }
        node = node.parentElement;
      }
      schedule();
    };

    ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    ro?.observe(document.documentElement);

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('pointerover', onOver, true);
    schedule();

    const mo =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => schedule())
        : null;
    mo?.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'data-wp-overlay-open',
        'data-wp-mist-scroll',
        'data-wp-scroll-area',
        'class',
        'aria-expanded',
        'aria-hidden',
        'hidden',
        'inert',
      ],
    });

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('pointerover', onOver, true);
      ro?.disconnect();
      mo?.disconnect();
      if (raf) cancelAnimationFrame(raf);
      for (const entry of rails.values()) entry.rail.remove();
      rails.clear();
    };
  }, []);
}
